<template>
  <StudioSpinner v-if="isLoadingFirstTime" />
  <StudioEmptyState v-else-if="totalCount === 0 && !isLoadingFirstTime && !isLoadingVideoAssets && !isFilterApplied">
    <template #title>No videos found</template>
    <template #content> Upload a video to get started. </template>
    <template #cta>
      <VideoLibraryUploadButton ref="uploadButton" :uploadFlow="uploadFlow" @upload:success="onUploadSuccess" />
    </template>
  </StudioEmptyState>
  <div v-else class="tw-relative tw-flex tw-h-full tw-flex-col">
    <!-- Header with filters -->
    <div class="tw-px-4">
      <div class="tw-flex tw-items-center tw-justify-between tw-pb-2 tw-pt-3">
        <div class="tw-flex tw-items-center tw-gap-2">
          <StudioSearchInput :searchTerm="search" placeholder="Search by title..." @onSearch="onSearch" />
          <StudioMultiselect
            v-if="!isEURegion()"
            :shouldShowSearchInput="false"
            :isOpen="isSourceSelectFilterOpen"
            :multiselectItems="sourceSelectFilterItems"
            @onToggleSelectItem="handleToggleSourceSelection"
            @close="isSourceSelectFilterOpen = false"
          >
            <template #trigger>
              <StudioMultiselectTriggerButton
                :selectedItemsTitles="selectedSourceNames"
                :suffix="{ singular: 'Source', plural: 'Sources' }"
                :characterLengthThreshold="60"
                @open="isSourceSelectFilterOpen = !isSourceSelectFilterOpen"
                @clear="handleClearSourceSelection"
              />
            </template>
          </StudioMultiselect>
          <VideoLibraryCreatedByFilter
            :selectedUserIds="selectedCreatedByIds"
            @update:selectedUserIds="handleCreatedByFilterChange"
          />
          <StudioMultiselect
            :isLoadingData="isFetchingEvents"
            shouldShowSearchInput
            :isOpen="isEventListFilterOpen"
            :multiselectItems="eventListFilterItems"
            :searchTerm="eventSelectionFilterSearchTerm"
            extraClasses="!tw-w-[360px]"
            @onToggleSelectItem="handleToggleEventSelection"
            @close="isEventListFilterOpen = false"
            @onSearch="handleEventListSearch"
          >
            <template #trigger>
              <StudioMultiselectTriggerButton
                :selectedItemsTitles="selectedEventNames"
                :suffix="{ singular: 'Event', plural: 'Events' }"
                :characterLengthThreshold="60"
                @open="isEventListFilterOpen = !isEventListFilterOpen"
                @clear="handleClearEventSelection"
              />
            </template>
            <template #empty-state>
              <div class="tw-flex tw-items-center tw-justify-center tw-py-4">
                <p class="tw-text-xs tw-text-slate-500">No results found!</p>
              </div>
            </template>
          </StudioMultiselect>
          <StudioMultiselect
            :isLoadingData="isFetchingTags"
            shouldShowSearchInput
            :isOpen="isTagsFilterOpen"
            :multiselectItems="tagsFilterItems"
            :searchTerm="tagSelectionFilterSearchTerm"
            @onToggleSelectItem="handleToggleTagsSelection"
            @close="isTagsFilterOpen = false"
            @onSearch="handleTagsSearch"
          >
            <template #trigger>
              <StudioMultiselectTriggerButton
                :selectedItemsTitles="selectedTagsNames"
                :suffix="{ singular: 'Tag', plural: 'Tags' }"
                :characterLengthThreshold="60"
                @open="isTagsFilterOpen = !isTagsFilterOpen"
                @clear="handleClearTagsSelection"
              />
            </template>
            <template #empty-state>
              <div class="tw-flex tw-items-center tw-justify-center tw-py-4">
                <p class="tw-text-xs tw-text-slate-500">No results found!</p>
              </div>
            </template>
          </StudioMultiselect>
          <StudioMultiselect
            :isLoadingData="isFetchingSeries"
            shouldShowSearchInput
            :isOpen="isSeriesFilterOpen"
            :multiselectItems="seriesFilterItems"
            :searchTerm="seriesSelectionFilterSearchTerm"
            @onToggleSelectItem="handleToggleSeriesSelection"
            @close="isSeriesFilterOpen = false"
            @onSearch="handleSeriesSearch"
          >
            <template #trigger>
              <StudioMultiselectTriggerButton
                :selectedItemsTitles="selectedSeriesNames"
                :suffix="{ singular: 'Series', plural: 'Series' }"
                :characterLengthThreshold="60"
                @open="isSeriesFilterOpen = !isSeriesFilterOpen"
                @clear="handleClearSeriesSelection"
              />
            </template>
            <template #empty-state>
              <div class="tw-flex tw-items-center tw-justify-center tw-py-4">
                <p class="tw-text-xs tw-text-slate-500">No results found!</p>
              </div>
            </template>
          </StudioMultiselect>
          <WorkspaceSelectFilter
            v-if="isWorkspaceEnabled && !props.workspaceFilter?.hidden"
            :showNoWorkspaceOption="false"
            dropdownVariant="outline"
            :selectedWorkspace="props.workspaceFilter?.id"
            @onSelectWorkspaceFilter="onSelectWorkspaceFilter"
          />
        </div>
        <VideoLibraryUploadButton
          ref="uploadButton"
          :buttonVariant="selectable ? 'outline' : 'filled'"
          :uploadFlow="uploadFlow"
          @upload:success="onUploadSuccess"
        />
      </div>
      <div data-testid="video-library-table-columns" class="tw-flex tw-w-full tw-border-b">
        <div
          v-for="column in columns"
          :key="column.key"
          :class="{
            'tw-flex tw-items-center tw-px-1 tw-py-2 tw-text-xs tw-font-normal': true,

            // Classes based on column type
            'tw-w-[3%]': column.key === 'select',
            'tw-w-[30%]': column.key === 'name',
            'tw-w-[13%]': column.key === 'created_at',
            'tw-w-[10%]': column.key === 'status',
            'tw-hidden md:tw-flex md:tw-flex-1': column.key === 'source' || column.key === 'created_by',
            'tw-hidden lg:tw-flex lg:tw-flex-1': column.key === 'workspace',
            'tw-flex-1 tw-justify-center': column.key === 'actions',

            // Additional classes
            'tw-gap-2': column.sortable
          }"
        >
          {{ column.label }}
          <SortButton
            v-if="column.sortable"
            :trackingId="`video-library-table-${column.key}-sort-button`"
            :isActive="sortOrder === column.key || sortOrder === `-${column.key}`"
            :sortOrder="sortOrder === `-${column.key}` ? 'asc' : sortOrder === column.key ? 'desc' : 'none'"
            @sortBy="handleSort(column.key as SortableKey, $event)"
          />
        </div>
      </div>
    </div>
    <!-- Body - Scrollable area -->
    <div ref="scrollableArea" class="tw-flex-1 tw-overflow-y-auto tw-px-4">
      <div v-if="videoAssets.length > 0">
        <div
          v-for="(video, index) in videoAssets"
          :key="video.id"
          :class="{
            'tw-flex tw-w-full hover:tw-bg-slate-50': true,
            'tw-border-b': index !== videoAssets.length - 1,
            'tw-cursor-pointer':
              props.selectable || ((isVideoLibraryOpenedInContentLab || isVideoPage) && getIsVideoAssetEditable(video)),
            '!tw-cursor-default': props.selectable && !video.selectable,
            'tw-pointer-events-none tw-opacity-50': props.selectable && video.status === 'READY_TO_PUBLISH'
          }"
          data-testid="video-library-table-row"
          @click="onClickVideoAssetRow(video, $event)"
          @mouseenter="onMouseEnterVideoAssetRow(video)"
          @mouseleave="hoveringVideoId = null"
        >
          <div
            v-for="column in columns"
            :key="column.key"
            :class="{
              'tw-flex tw-items-center tw-px-1 tw-py-2 tw-text-xs tw-font-normal': true,

              // Classes based on column type
              'tw-w-[3%]': column.key === 'select',
              'tw-w-[30%] tw-gap-2': column.key === 'name',
              'tw-w-[13%]': column.key === 'created_at',
              'tw-w-[10%]': column.key === 'status',
              'tw-hidden md:tw-flex md:tw-flex-1': column.key === 'source' || column.key === 'created_by',
              'tw-hidden lg:tw-flex lg:tw-flex-1': column.key === 'workspace',
              'tw-flex-1 tw-justify-center': column.key === 'actions'
            }"
          >
            <template v-if="column.key === 'select'">
              <StudioCheckbox
                v-if="props.maxSelections > 1"
                id="video-library-table-select-checkbox"
                :checked="selectedVideoIds.includes(video.id)"
                :disabled="(isMaxSelectionsReached && !selectedVideoIds.includes(video.id)) || !video.selectable"
              />
              <input
                v-else
                type="radio"
                data-testid="video-library-table-select-radio"
                :checked="selectedVideoIds.includes(video.id)"
                :disabled="!video.selectable"
                :class="{
                  'tw-text-deep-orange-500 checked:tw-bg-deep-orange-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-deep-orange-500 focus:tw-ring-offset-2': true,
                  'tw-cursor-not-allowed tw-text-slate-300': !video.selectable,
                  'tw-cursor-pointer': video.selectable
                }"
              />
            </template>
            <template v-if="column.key === 'name'">
              <div class="tw-flex tw-h-full tw-w-full tw-items-center tw-gap-2 tw-overflow-visible tw-pr-6">
                <VideoLibraryItemThumbnail :videoAsset="video" />
                <div class="tw-flex tw-h-full tw-w-full tw-grow tw-flex-col tw-justify-center tw-overflow-hidden">
                  <span class="tw-line-clamp-3 tw-shrink-0 tw-break-words tw-text-xs">{{ video.name }}</span>
                </div>
                <VideoLibraryTableItemUsageHover
                  v-if="video.usageCount > 0 && isUseCLUploadsInVLEnabled"
                  :videoAsset="video"
                  :tooltipDirection="getTooltipOpenDirection(index)"
                />
              </div>
            </template>

            <template v-else-if="column.key === 'created_at'">
              <span class="tw-text-xs">{{ video.createdAt }}</span>
            </template>

            <template v-else-if="column.key === 'status'">
              <VideoLibraryTableStatusCell
                :videoAsset="video"
                :tooltipPosition="getTooltipOpenDirection(index) === 'down' ? 'bottom' : 'top'"
              />
            </template>

            <template v-else-if="column.key === 'source'">
              <span class="tw-text-xs">{{ transformVideoAssetSource(video.source) }}</span>
            </template>

            <template v-else-if="column.key === 'created_by'">
              <span class="tw-text-xs">{{ video?.createdBy }}</span>
            </template>

            <template v-else-if="column.key === 'workspace'">
              <span class="tw-text-xs">{{ workspaceNameIdMap[video?.workspace || ''] }}</span>
            </template>

            <template v-else-if="column.key === 'actions' && video.status !== 'READY_TO_PUBLISH'">
              <VideoLibraryTableItemQuickActions
                v-if="hoveringVideoId === video.id"
                :videoAsset="video"
                :selected="selectedVideoIds.includes(video.id)"
                :openDirection="getTooltipOpenDirection(index) === 'down' ? 'bottom-right' : 'top-right'"
                @trigger:rename="renamingVideoAsset = video"
                @trigger:preview="hlsPreviewVideoAsset = video"
                @trigger:delete="onTriggerDelete"
                @trigger:download="onTriggerDownload"
                @trigger:download-seo-geo="video => downloadVideoMetaDataForVL(video.id, video.name)"
                @trigger:edit="onTriggerEdit"
              />
            </template>
          </div>
        </div>
      </div>
      <div
        v-else-if="!isLoadingVideoAssets"
        class="tw-flex tw-h-full tw-items-center tw-justify-center tw-text-center tw-text-sm tw-text-gray-500"
      >
        <p>
          <template v-if="search"> No videos found matching "{{ search }}" </template>
          <template v-else> No videos found </template>
        </p>
      </div>
    </div>
    <!-- Footer -->
    <div v-if="totalCount > pageSize" class="tw-border-t">
      <VideoLibraryTablePagination
        :pageSize="pageSize"
        :totalCount="totalCount"
        :currentPage="currentPage"
        @fetch="onFetchVideoAsset"
      />
    </div>

    <StudioSpinner v-if="isUploadingNewFile || isLoadingVideoAssets" coverContainer overlay />
    <VideoLibraryRenamePopup
      v-if="renamingVideoAsset"
      :videoAsset="renamingVideoAsset"
      @close="renamingVideoAsset = null"
      @success:rename="onRenameSuccess"
    />
    <template v-if="deletingVideoAsset">
      <StudioConfirmationDialog
        v-if="deletingVideoAsset.creationType === 'CLIP'"
        type="error"
        heading="Delete clip from Video Library"
        :actionsDisabled="isDeletingVideoAsset"
        message="This will delete the video only from the Video Library. You can still access the video in Content Lab."
        :primaryActionTitle="isDeletingVideoAsset ? 'Deleting...' : 'Delete'"
        secondaryActionTitle="Cancel"
        @primaryAction="handleDeleteVideoAsset"
        @secondaryAction="deletingVideoAsset = null"
      />
      <StudioConfirmationDialog
        v-else
        type="error"
        heading="Are you sure?"
        :actionsDisabled="isDeletingVideoAsset"
        :message="`You are deleting the video <b>${deletingVideoAsset.name}</b>. This action cannot be undone.`"
        :primaryActionTitle="isDeletingVideoAsset ? 'Deleting...' : 'Delete'"
        secondaryActionTitle="Cancel"
        @primaryAction="handleDeleteVideoAsset"
        @secondaryAction="deletingVideoAsset = null"
      />
    </template>
    <HLSVideoPreviewDialog
      v-if="hlsPreviewVideoAsset"
      :videoAsset="hlsPreviewVideoAsset"
      @close="hlsPreviewVideoAsset = null"
    />
    <VideoLibraryUsageDialog v-if="usageData && usageData.length" :usageData="usageData" @close="usageData = null" />
  </div>
</template>

<script setup lang="ts">
import {
  ListAPIQueryParams,
  listEvents,
  listTagLibrary,
  Tag,
  listSeries,
  listVideoAssetUsage,
  VideoAssetUsage
} from '@goldcast/api/backend';
import logger from '@goldcast/logger';
import throttle from 'lodash.throttle';
import { computed, onMounted, ref, Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import {
  deleteVideoAsset,
  getVideoLibraryAssets,
  VideoLibrarySortOrder,
  getValidVLSource,
  type VideoAsset,
  downloadVideoAsset,
  downloadVideoMetaDataForVL,
  EDIT_VIDEO_ERROR_CODES_DESCRIPTION,
  editConfigByVideoId,
  setEditConfigByVideoId,
  getIsVideoAssetEditable,
  validateEditConfigByVideoId
} from '../videoLibraryService';
import VideoLibraryCreatedByFilter from './filters/VideoLibraryCreatedByFilter.vue';
import VideoLibraryItemThumbnail from './VideoLibraryItemThumbnail.vue';
import VideoLibraryRenamePopup from './VideoLibraryRenamePopup.vue';
import VideoLibraryTableItemQuickActions from './VideoLibraryTableItemQuickActions.vue';
import VideoLibraryTableItemUsageHover from './VideoLibraryTableItemUsageHover.vue';
import VideoLibraryTableStatusCell from './VideoLibraryTableStatusCell.vue';
import HLSVideoPreviewDialog from '../HLSVideoPreviewDialog.vue';
import VideoLibraryTablePagination from './VideoLibraryTablePagination.vue';
import VideoLibraryUploadButton from './VideoLibraryUploadButton.vue';
import VideoLibraryUsageDialog from './VideoLibraryUsageDialog.vue';
import { transformVideoAssetSource, VideoUploadContext } from '../utils';

import WorkspaceSelectFilter from '@/studio/workspace/WorkspaceSelectFilter.vue';
import SortButton from '@/studioComponents/SortButton.vue';
import StudioCheckbox from '@/studioComponents/StudioCheckbox.vue';
import StudioConfirmationDialog from '@/studioComponents/StudioConfirmationDialog.vue';
import StudioEmptyState from '@/studioComponents/StudioEmptyState.vue';
import StudioMultiselect, { MultiselectItem } from '@/studioComponents/StudioMultiselect.vue';
import StudioMultiselectTriggerButton from '@/studioComponents/StudioMultiselectTriggerButton.vue';
import StudioSearchInput from '@/studioComponents/StudioSearchInput.vue';
import StudioSpinner from '@/studioComponents/StudioSpinner.vue';
import { isEURegion } from '@/utils';
import { isCLEUProcessingEnabled } from '@/utils/clFeatureGating';
import { getTimeRangeString } from '@/utils/date';

export type VideoLibraryWorkspaceFilter = {
  id: ListAPIQueryParams['workspace'];
  hidden?: boolean;
};

type UploadFlow = 'legacy' | 'content-lab-federated' | 'content-lab-import';

const props = withDefaults(
  defineProps<{
    pageSize: number;
    currentPage: number;
    sortOrder: VideoLibrarySortOrder;
    search: string;
    selectable?: boolean;
    maxSelections?: number;
    createdBy: string[];
    initialSelectedVideoAssets?: VideoAsset[];
    sources: ListAPIQueryParams['source'];
    events: ListAPIQueryParams['event_ids'][];
    tags: ListAPIQueryParams['event_tag'];
    series: ListAPIQueryParams['series_id'][];
    workspaceFilter: VideoLibraryWorkspaceFilter | undefined;
    context: VideoUploadContext;
  }>(),
  {
    pageSize: 50,
    currentPage: 1,
    sortOrder: '-created_at',
    search: '',
    selectable: false,
    maxSelections: Infinity,
    initialSelectedVideoAssets: () => [],
    context: ''
  }
);

const TOOLTIP_POSITION_THRESHOLD = 3;

type SortableKey = 'name' | 'created_at';

const store = useStore();
const isLoadingFirstTime = ref(false);
const isLoadingVideoAssets = ref(false);
const videoAssets = ref<VideoAsset[]>([]);
const totalCount = ref(0);
const hoveringVideoId = ref<string | null>(null);
const renamingVideoAsset = ref<VideoAsset | null>(null);
const deletingVideoAsset = ref<VideoAsset | null>(null);
const isDeletingVideoAsset = ref(false);
const hlsPreviewVideoAsset = ref<VideoAsset | null>(null);
const selectedVideoAssets = ref<VideoAsset[]>(props.initialSelectedVideoAssets || []);
const isSourceSelectFilterOpen = ref(false);
const isEventListFilterOpen = ref(false);
const isTagsFilterOpen = ref(false);
const isSeriesFilterOpen = ref(false);
const isFetchingEvents = ref(false);
const isFetchingTags = ref(false);
const isFetchingSeries = ref(false);
const eventSelectionFilterSearchTerm = ref('');
const tagSelectionFilterSearchTerm = ref('');
const seriesSelectionFilterSearchTerm = ref('');
const usageData = ref<VideoAssetUsage[] | null>(null);

const isWorkspaceEnabled = computed(() => store?.getters['org/getIsWorkspaceEnabled']);
const isVideoLibraryClipPublishing = computed(() => store?.getters['org/getCurrentOrg']?.showVideoLibraryClipPublish);
const isUseCLUploadsInVLEnabled = computed(
  () => store?.getters['org/getCurrentOrg']?.Use_CL_Uploads_In_VL && !isEURegion()
);

const route = useRoute();
const router = useRouter();

const workspaceNameIdMap = computed(() => store?.getters['workspace/getWorkspaceNameIdMap'] || {});

const isVideoPage = computed(() => route.path === '/videos');

const isVideoLibraryOpenedInContentLab = computed(() => route.path.includes('/content'));

const uploadFlow = computed<UploadFlow>(() => {
  if (isEURegion() || !isCLEUProcessingEnabled()) {
    return 'legacy';
  }

  if (
    isUseCLUploadsInVLEnabled.value &&
    ['video-library-page', 'registration-page', 'hub-page'].includes(props.context)
  ) {
    return 'content-lab-federated';
  }

  if (props.context === 'content-lab') {
    return 'content-lab-import';
  }

  return 'legacy';
});

const columns = [
  ...(props.selectable ? [{ key: 'select', label: '', sortable: false }] : []),
  { key: 'name', label: 'Title', sortable: true },
  { key: 'created_at', label: 'Created At', sortable: true },
  { key: 'status', label: 'Status', sortable: false },
  ...(!isEURegion() ? [{ key: 'source', label: 'Source', sortable: false }] : []),
  { key: 'created_by', label: 'Created By', sortable: false },
  ...(isWorkspaceEnabled.value ? [{ key: 'workspace', label: 'Team Workspace', sortable: false }] : []),
  { key: 'actions', label: '', sortable: false }
] as { key: string; label: string; sortable: boolean }[];

const sourceSelectFilterItems = ref<MultiselectItem[]>([
  {
    id: 'EVENT_RECORDING',
    title: 'Event Recording',
    selected: props.sources?.includes('EVENT_RECORDING') || false
  },
  ...(isCLEUProcessingEnabled()
    ? [
        {
          id: 'DIRECT_RECORDING',
          title: 'Recording Studio',
          selected: props.sources?.includes('DIRECT_RECORDING') || false
        }
      ]
    : []),
  ...(isUseCLUploadsInVLEnabled.value && isCLEUProcessingEnabled()
    ? [
        {
          id: 'CL_UPLOAD',
          title: 'Upload',
          selected: props.sources?.includes('CL_UPLOAD') || false
        }
      ]
    : []),
  ...(!isVideoLibraryOpenedInContentLab.value
    ? [
        {
          id: 'UPLOADED',
          title: isUseCLUploadsInVLEnabled.value ? 'Event Asset' : 'Manual Upload',
          selected: props.sources?.includes('UPLOADED') || false
        }
      ]
    : []),
  ...(isVideoLibraryClipPublishing.value && !isVideoLibraryOpenedInContentLab.value && isCLEUProcessingEnabled()
    ? [
        {
          id: 'CLIP',
          title: isUseCLUploadsInVLEnabled.value ? 'Clip' : 'Content Lab Clip',
          selected: props.sources?.includes('CLIP') || false
        }
      ]
    : [])
]);

const selectedCreatedByIds = ref<string[]>(props.createdBy);
const eventListFilterItems = ref<MultiselectItem[]>([]);
const tagsFilterItems = ref<MultiselectItem[]>([]);
const seriesFilterItems = ref<MultiselectItem[]>([]);

const selectedSourceNames = computed(() => {
  return sourceSelectFilterItems.value.filter(item => item.selected).map(item => item.title);
});

const selectedEventNames = computed(() => {
  return eventListFilterItems.value.filter(item => item.selected).map(item => item.title);
});

const selectedTagsNames = computed(() => {
  return tagsFilterItems.value.filter(item => item.selected).map(item => item.title);
});

const selectedSeriesNames = computed(() => {
  return seriesFilterItems.value.filter(item => item.selected).map(item => item.title);
});

const isFilterApplied = computed(() => {
  return (
    selectedSourceNames.value.length > 0 ||
    selectedCreatedByIds.value.length > 0 ||
    props.search ||
    selectedEventNames.value.length > 0 ||
    selectedTagsNames.value.length > 0 ||
    selectedSeriesNames.value.length > 0 ||
    props.workspaceFilter
  );
});

const emit = defineEmits<{
  (e: 'update:currentPage', page: number): void;
  (e: 'update:sortOrder', order: VideoLibrarySortOrder): void;
  (e: 'update:search', search: string): void;
  (e: 'update:selectedVideoAssets', selectedVideoAssets: VideoAsset[]): void;
  (e: 'update:sources', sources: ListAPIQueryParams['source']): void;
  (e: 'update:createdBy', createdBy: string[]): void;
  (e: 'update:events', createdBy: ListAPIQueryParams['event_ids']): void;
  (e: 'update:tags', tags: ListAPIQueryParams['event_tag']): void;
  (e: 'update:series', series: ListAPIQueryParams['series_id']): void;
  (e: 'update:workspaceFilter', workspaceFilter: VideoLibraryWorkspaceFilter): void;
}>();

defineExpose({
  selectedVideoAssets
});

const uploadButton = ref<InstanceType<typeof VideoLibraryUploadButton>>();
const isUploadingNewFile = computed(() => uploadButton.value?.isUploadingNewFile);

const selectedVideoIds = computed(() => selectedVideoAssets.value.map(video => video.id));

const isMaxSelectionsReached = computed(() => {
  return selectedVideoAssets.value.length >= props.maxSelections;
});

const getTooltipOpenDirection = (index: number) => {
  if (videoAssets.value.length <= TOOLTIP_POSITION_THRESHOLD) {
    return 'down';
  }

  if (index >= videoAssets.value.length - TOOLTIP_POSITION_THRESHOLD) {
    return 'up';
  }

  return 'down';
};

const scrollableArea = ref<HTMLDivElement | null>(null);

onMounted(() => {
  fetchVideoAssets({
    firstLoad: true,
    currentPage: props.currentPage,
    sortOrder: props.sortOrder,
    search: props.search,
    sources: props.sources,
    events: props.events,
    tags: props.tags,
    series: props.series,
    workspaceFilter: props.workspaceFilter,
    createdBy: props.createdBy
  });
  fetchEventList('');
  fetchTagsList('');
  fetchSeriesList('');
});

const fetchVideoAssets = async ({
  firstLoad,
  currentPage,
  sortOrder,
  search,
  sources,
  createdBy,
  events,
  tags,
  series,
  workspaceFilter
}: {
  firstLoad: boolean;
  currentPage: number;
  sortOrder: VideoLibrarySortOrder;
  search: string;
  sources: ListAPIQueryParams['source'];
  createdBy?: string[];
  events: ListAPIQueryParams['event_ids'][];
  tags: ListAPIQueryParams['event_tag'];
  series: ListAPIQueryParams['series_id'][];
  workspaceFilter?: VideoLibraryWorkspaceFilter;
}) => {
  try {
    if (firstLoad) {
      isLoadingFirstTime.value = true;
    }

    isLoadingVideoAssets.value = true;

    let sourcesForVariant = sources;
    if (isVideoLibraryOpenedInContentLab.value) {
      // Note: When showing VL in Content Lab, we must explicitly hide UPLOADED (Legacy Uploads)
      if (!sourcesForVariant?.length) {
        sourcesForVariant = ['CL_UPLOAD', 'EVENT_RECORDING', 'DIRECT_RECORDING'];
      } else {
        sourcesForVariant = sourcesForVariant.filter(
          source => source === 'CL_UPLOAD' || source === 'EVENT_RECORDING' || source === 'DIRECT_RECORDING'
        );
      }
    }

    const response = await getVideoLibraryAssets({
      limit: props.pageSize,
      offset: (currentPage - 1) * props.pageSize,
      search,
      ordering: sortOrder,
      workspace: workspaceFilter?.id,
      source: sourcesForVariant,
      created_by: createdBy as unknown as ListAPIQueryParams['created_by'],
      event_ids: events,
      event_tag: tags,
      series_id: series
    });

    videoAssets.value = response.assets;

    if (response.totalCount > 0 && videoAssets.value.length === 0 && currentPage > 1) {
      fetchVideoAssets({
        firstLoad: true,
        currentPage: 1,
        sortOrder: props.sortOrder,
        search,
        sources,
        createdBy,
        events,
        tags,
        series,
        workspaceFilter: props.workspaceFilter
      });
      return;
    }

    totalCount.value = response.totalCount;
    const actualCurrentPage =
      currentPage > Math.ceil(totalCount.value / props.pageSize)
        ? Math.ceil(totalCount.value / props.pageSize)
        : currentPage;

    emit('update:currentPage', actualCurrentPage);
  } catch (error: any) {
    store.dispatch('studioToast/triggerToast', {
      mode: 'error',
      msg: error?.message || 'Unable to fetch videos. Please try again.'
    });
  } finally {
    isLoadingFirstTime.value = false;
    isLoadingVideoAssets.value = false;
    scrollableArea.value?.scrollTo?.({ top: 0, left: 0, behavior: 'smooth' });
  }
};

const onFetchVideoAsset = ({ firstLoad, currentPage }: { firstLoad: boolean; currentPage: number }) => {
  fetchVideoAssets({
    firstLoad,
    currentPage,
    sortOrder: props.sortOrder,
    search: props.search,
    ...getUpdatedMultiselectFilters(),
    workspaceFilter: props.workspaceFilter
  });
};

const fetchEventList = async (searchTerm: string) => {
  isFetchingEvents.value = true;
  const currentOrg = store.getters['org/getCurrentOrg'];
  const events = await listEvents({
    queryParams: {
      organization: currentOrg.id,
      title: searchTerm,
      limit: 25
    }
  });
  isFetchingEvents.value = false;
  if (!events || !events.results) {
    return;
  }
  eventListFilterItems.value = events?.results?.map(event => ({
    id: event.id,
    title: event.title,
    subtitle: getTimeRangeString({
      date1: event.start_time,
      date2: event.end_time,
      timezone: event.timezone
    }),
    selected: props.events.includes(event.id) || false
  }));
};

const fetchTagsList = async (searchTerm: string) => {
  isFetchingTags.value = true;
  const currentOrg = store.getters['org/getCurrentOrg'];
  let tags = (await listTagLibrary({
    queryParams: {
      organization: currentOrg.id
    }
  })) as Tag[];
  if (!tags) {
    return;
  }
  if (searchTerm && searchTerm.length > 0) {
    tags = tags?.filter(tag => tag?.tag_name?.toLowerCase().includes(searchTerm.toLowerCase()));
  }
  tagsFilterItems.value = tags?.map(tag => ({
    id: tag.id,
    title: tag?.tag_name || '',
    selected: props?.tags?.includes(tag.id) || false
  }));
  isFetchingTags.value = false;
};

const fetchSeriesList = async (searchTerm: string) => {
  isFetchingSeries.value = true;
  const currentOrg = store.getters['org/getCurrentOrg'];
  const series = await listSeries({
    queryParams: {
      organization: currentOrg.id,
      name: searchTerm,
      limit: 25
    }
  });
  isFetchingSeries.value = false;
  if (!series || !series.results) {
    return;
  }
  seriesFilterItems.value = series?.results?.map(series => ({
    id: series.id,
    title: series.name || '',
    selected: props.series.includes(series.id) || false
  }));
};

const handleSort = (field: SortableKey, order: 'asc' | 'desc') => {
  const sortMapping = {
    name: { asc: '-name', desc: 'name' },
    created_at: { asc: '-created_at', desc: 'created_at' }
  };

  const newSortOrder = sortMapping[field][order] as VideoLibrarySortOrder;
  emit('update:sortOrder', newSortOrder);
  fetchVideoAssets({
    firstLoad: false,
    currentPage: 1,
    sortOrder: newSortOrder,
    search: props.search,
    ...getUpdatedMultiselectFilters(),
    workspaceFilter: props.workspaceFilter
  });
};

const clearAllFilters = () => {
  clearMultiselectFilterSelection(sourceSelectFilterItems, 'sources', false);
  clearMultiselectFilterSelection(eventListFilterItems, 'events', false);
  clearMultiselectFilterSelection(tagsFilterItems, 'tags', false);
  clearMultiselectFilterSelection(seriesFilterItems, 'series', false);

  clearCreatedByFilter();

  emit('update:workspaceFilter', { id: undefined });
};

const onUploadSuccess = (newVideoAsset?: VideoAsset) => {
  clearAllFilters();

  if (newVideoAsset) {
    videoAssets.value.unshift(newVideoAsset);
    totalCount.value = totalCount.value + 1;
  }

  fetchVideoAssets({
    firstLoad: false,
    currentPage: 1,
    sortOrder: props.sortOrder,
    search: props.search,
    ...getUpdatedMultiselectFilters(),
    workspaceFilter: props.workspaceFilter
  });
};

const onRenameSuccess = (newVideoAsset: VideoAsset) => {
  renamingVideoAsset.value = null;
  videoAssets.value = videoAssets.value.map(video => {
    if (video.id === newVideoAsset.id) {
      return newVideoAsset;
    }
    return video;
  });
};

const onTriggerDownload = (videoAsset: VideoAsset) => {
  downloadVideoAsset(videoAsset);
};

const handleDeleteVideoAsset = () => {
  if (!deletingVideoAsset.value) {
    return;
  }

  const videoIdToDelete = deletingVideoAsset.value.id;
  isDeletingVideoAsset.value = true;

  deleteVideoAsset(videoIdToDelete)
    .then(() => {
      store.dispatch('studioToast/triggerToast', {
        mode: 'success',
        msg: 'Video deleted successfully.'
      });
      document.dispatchEvent(new CustomEvent('ReloadCLHomepageMediaContent'));
      videoAssets.value = videoAssets.value.filter(video => video.id !== videoIdToDelete);
      totalCount.value = totalCount.value - 1;
      delete editConfigByVideoId[videoIdToDelete];
    })
    .catch((errorMessage: string) => {
      store.dispatch('studioToast/triggerToast', { mode: 'error', msg: errorMessage });
    })
    .finally(() => {
      isDeletingVideoAsset.value = false;
      deletingVideoAsset.value = null;
    });
};

const onClickVideoAssetRow = (video: VideoAsset, event?: MouseEvent) => {
  if (isVideoLibraryOpenedInContentLab.value || isVideoPage.value) {
    onTriggerEdit(video, event);
    return;
  }

  handleSelectVideoAsset(video);
};

const handleSelectVideoAsset = (video: VideoAsset) => {
  if (!video.selectable) {
    return;
  }

  if (isMaxSelectionsReached.value && !selectedVideoIds.value.includes(video.id)) {
    if (props.maxSelections === 1) {
      selectedVideoAssets.value = [video];
      emit('update:selectedVideoAssets', selectedVideoAssets.value);
      return;
    }

    return;
  }

  selectedVideoAssets.value = selectedVideoIds.value.includes(video.id)
    ? selectedVideoAssets.value.filter(v => v.id !== video.id)
    : [...selectedVideoAssets.value, video];
  emit('update:selectedVideoAssets', selectedVideoAssets.value);
};

const throttledSetEditLink = throttle((video: VideoAsset) => {
  if (editConfigByVideoId[video.id]) {
    validateEditConfigByVideoId(video);
  } else {
    setEditConfigByVideoId(video);
  }
}, 500);

const onMouseEnterVideoAssetRow = (video: VideoAsset) => {
  hoveringVideoId.value = video.id;

  if (!getIsVideoAssetEditable(video)) {
    return;
  }

  throttledSetEditLink(video);
};

const openEditLink = async (videoAsset: VideoAsset, event?: MouseEvent) => {
  const editConfig = editConfigByVideoId[videoAsset.id];

  if (editConfig.errorCode) {
    const errorMessage =
      EDIT_VIDEO_ERROR_CODES_DESCRIPTION[editConfig.errorCode] || EDIT_VIDEO_ERROR_CODES_DESCRIPTION['UNKNOWN_ERROR'];
    store.dispatch('studioToast/triggerToast', {
      mode: 'error',
      msg: errorMessage
    });
    return;
  }

  if (!editConfig.url) {
    await setEditConfigByVideoId(videoAsset);
    openEditLink(videoAsset, event);
    return;
  }

  const isModifierKeyPressed = event && (event.metaKey || event.ctrlKey);

  if (isVideoLibraryOpenedInContentLab.value && !isModifierKeyPressed) {
    store.dispatch('contentStudio/editVideoAssetInContentLab', {
      contentId: editConfig.contentId,
      projectId: editConfig.projectId,
      url: editConfig.url
    });
    return;
  }

  if (isVideoPage.value && !isModifierKeyPressed) {
    router.push(editConfig.url);
    return;
  }

  // Open in new tab if modifier key is pressed or if not in special contexts
  window.open(`${window.location.origin}${editConfig.url}`, '_blank');
};

const onTriggerEdit = async (videoAsset: VideoAsset, event?: MouseEvent) => {
  if (!getIsVideoAssetEditable(videoAsset)) {
    return;
  }

  if (editConfigByVideoId[videoAsset.id]) {
    openEditLink(videoAsset, event);
    return;
  }

  await setEditConfigByVideoId(videoAsset);

  openEditLink(videoAsset, event);
};

const handleEventListSearch = (search: string) => {
  eventSelectionFilterSearchTerm.value = search;
  fetchEventList(search);
};

const handleTagsSearch = (search: string) => {
  tagSelectionFilterSearchTerm.value = search;
  fetchTagsList(search);
};

const handleSeriesSearch = (search: string) => {
  seriesSelectionFilterSearchTerm.value = search;
  fetchSeriesList(search);
};

const onSearch = (search: string) => {
  emit('update:search', search);

  fetchVideoAssets({
    firstLoad: false,
    currentPage: 1,
    sortOrder: props.sortOrder,
    search,
    ...getUpdatedMultiselectFilters(),
    workspaceFilter: props.workspaceFilter
  });
};

const handleMultiselectFilterSelection = (
  filterItems: Ref<MultiselectItem[]>,
  emitKey: 'sources' | 'events' | 'tags' | 'series'
) => {
  return itemId => {
    filterItems.value = filterItems.value.map(item => ({
      ...item,
      selected: item.id === itemId ? !item.selected : item.selected
    }));

    if (filterItems.value.every(item => !item.selected)) {
      clearMultiselectFilterSelection(filterItems, emitKey);
      return;
    }

    const updatedFilter = filterItems.value.filter(item => item.selected).map(item => item.id);

    if (emitKey === 'sources') {
      emit('update:sources', updatedFilter as ListAPIQueryParams['source']);
    }
    if (emitKey === 'events') {
      emit('update:events', updatedFilter as unknown as ListAPIQueryParams['event_ids']);
    } else if (emitKey === 'tags') {
      emit('update:tags', updatedFilter as ListAPIQueryParams['event_tag']);
    } else if (emitKey === 'series') {
      emit('update:series', updatedFilter as unknown as ListAPIQueryParams['series_id']);
    }

    fetchVideoAssets({
      firstLoad: false,
      currentPage: 1,
      sortOrder: props.sortOrder,
      search: props.search,
      ...getUpdatedMultiselectFilters(),
      workspaceFilter: props.workspaceFilter,
      [emitKey]: updatedFilter
    });
  };
};

const clearMultiselectFilterSelection = (
  filterItems: Ref<MultiselectItem[]>,
  emitKey: 'sources' | 'createdBy' | 'events' | 'tags' | 'series',
  shouldFetchVideoAsset = true
) => {
  filterItems.value = filterItems.value.map(item => ({
    ...item,
    selected: false
  }));

  if (emitKey === 'sources') {
    emit(
      'update:sources',
      isVideoLibraryOpenedInContentLab.value ? getValidVLSource() : ([] as ListAPIQueryParams['source'])
    );
  } else if (emitKey === 'events') {
    emit('update:events', [] as unknown as ListAPIQueryParams['event_ids']);
  } else if (emitKey === 'tags') {
    emit('update:tags', [] as ListAPIQueryParams['event_tag']);
  } else if (emitKey === 'series') {
    emit('update:series', [] as unknown as ListAPIQueryParams['series_id']);
  }

  if (!shouldFetchVideoAsset) {
    return;
  }

  fetchVideoAssets({
    firstLoad: false,
    currentPage: 1,
    sortOrder: props.sortOrder,
    search: props.search,
    ...getUpdatedMultiselectFilters(),
    workspaceFilter: props.workspaceFilter
  });
};

const getUpdatedMultiselectFilters = () => {
  const sources = sourceSelectFilterItems.value
    .filter(item => item.selected)
    .map(item => item.id) as ListAPIQueryParams['source'];

  const createdBy = selectedCreatedByIds.value;

  const events = eventListFilterItems.value
    .filter(item => item.selected)
    .map(item => item.id) as unknown as ListAPIQueryParams['event_ids'][];

  const tags = tagsFilterItems.value
    .filter(item => item.selected)
    .map(item => item.id) as ListAPIQueryParams['event_tag'];

  const series = seriesFilterItems.value
    .filter(item => item.selected)
    .map(item => item.id) as unknown as ListAPIQueryParams['series_id'][];

  return {
    sources: sources && sources?.length > 0 ? sources : getValidVLSource(),
    createdBy,
    events,
    tags,
    series
  };
};

const handleToggleSourceSelection = handleMultiselectFilterSelection(sourceSelectFilterItems, 'sources');

const handleToggleEventSelection = handleMultiselectFilterSelection(eventListFilterItems, 'events');

const handleToggleTagsSelection = handleMultiselectFilterSelection(tagsFilterItems, 'tags');

const handleToggleSeriesSelection = handleMultiselectFilterSelection(seriesFilterItems, 'series');

const handleClearSourceSelection = () => {
  clearMultiselectFilterSelection(sourceSelectFilterItems, 'sources');
};

const handleClearEventSelection = () => {
  clearMultiselectFilterSelection(eventListFilterItems, 'events');
};

const handleClearTagsSelection = () => {
  clearMultiselectFilterSelection(tagsFilterItems, 'tags');
};

const handleClearSeriesSelection = () => {
  clearMultiselectFilterSelection(seriesFilterItems, 'series');
};

const onSelectWorkspaceFilter = (workspaceId: string | undefined) => {
  emit('update:workspaceFilter', { id: workspaceId });

  fetchVideoAssets({
    firstLoad: false,
    currentPage: 1,
    sortOrder: props.sortOrder,
    search: props.search,
    ...getUpdatedMultiselectFilters(),
    workspaceFilter: { id: workspaceId }
  });
};

const onTriggerDelete = (videoAsset: VideoAsset) => {
  // Premature return if the conditions aren't met
  if (
    !isVideoLibraryClipPublishing.value ||
    (videoAsset.creationType !== 'CLIP' && videoAsset.creationType !== 'UPLOADED')
  ) {
    deletingVideoAsset.value = videoAsset;
    return;
  }

  // If asset usage count is 0, return early
  if (videoAsset.usageCount === 0) {
    deletingVideoAsset.value = videoAsset;
    return;
  }

  // Fetch video asset usage if the conditions are met
  listVideoAssetUsage({ id: videoAsset.id })
    .then(response => {
      usageData.value = response;
    })
    .catch(error => {
      logger.error('Video Library Table: Error fetching video asset usage', error);
    });
};

const clearCreatedByFilter = () => {
  selectedCreatedByIds.value = [];
  emit('update:createdBy', []);
};

const handleCreatedByFilterChange = (userIds: string[]) => {
  selectedCreatedByIds.value = userIds;
  emit('update:createdBy', userIds);

  fetchVideoAssets({
    firstLoad: false,
    currentPage: 1,
    sortOrder: props.sortOrder,
    search: props.search,
    ...getUpdatedMultiselectFilters(),
    workspaceFilter: props.workspaceFilter,
    createdBy: userIds
  });
};
</script>
